import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Cloud, Loader2 } from "lucide-react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { GlassCard } from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  disconnectGoogleDrive,
  getGoogleDriveStatus,
  startGoogleDriveConnect,
  type GoogleDriveStatus,
} from "@/lib/api/google-drive";
import { APPS_HOME_PATH } from "@/lib/apps";

type ConnectionVisualState = "connected" | "warning" | "disabled";

function resolveConnectionVisualState(
  status: GoogleDriveStatus | null,
  reconnectRequired: boolean,
  hasActiveConnection: boolean,
): ConnectionVisualState {
  if (!status?.enabled) return "disabled";
  if (hasActiveConnection && !reconnectRequired) return "connected";
  return "warning";
}

const CONNECTION_VISUAL: Record<
  ConnectionVisualState,
  {
    badge: string;
    badgeClass: string;
    cardClass: string;
    iconClass: string;
    statusTextClass: string;
  }
> = {
  connected: {
    badge: "Connected",
    badgeClass:
      "border-emerald-400/40 bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
    cardClass: "border-emerald-500/35 bg-emerald-500/5",
    iconClass: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400",
    statusTextClass: "text-emerald-700 dark:text-emerald-300",
  },
  warning: {
    badge: "Action required",
    badgeClass: "border-amber-400/40 bg-amber-500/20 text-amber-700 dark:text-amber-300",
    cardClass: "border-amber-500/35 bg-amber-500/5",
    iconClass: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
    statusTextClass: "text-amber-700 dark:text-amber-200",
  },
  disabled: {
    badge: "Unavailable",
    badgeClass:
      "border-slate-400/40 bg-slate-500/15 text-[var(--lm-muted)] dark:border-slate-500/40 dark:bg-slate-500/15 dark:text-slate-400",
    cardClass: "border-[var(--lm-border-subtle)] dark:border-white/10",
    iconClass:
      "bg-[var(--surface-sunken)] text-[var(--lm-muted)] dark:bg-slate-500/15 dark:text-slate-400",
    statusTextClass: "text-[var(--lm-muted)] dark:text-slate-400",
  },
};

export function GoogleDriveIntegrationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<GoogleDriveStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const reconnectRequired = Boolean(status?.reconnectRequired);
  const hasActiveConnection = Boolean(status?.connected);
  const visualState = resolveConnectionVisualState(status, reconnectRequired, hasActiveConnection);
  const visual = CONNECTION_VISUAL[visualState];
  const statusBadgeLabel =
    visualState === "connected"
      ? "Connected"
      : reconnectRequired
        ? "Reconnect required"
        : visualState === "warning"
          ? "Not connected"
          : visual.badge;

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    setActionError(null);
    try {
      const next = await getGoogleDriveStatus();
      setStatus(next);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to load integration status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    const driveResult = searchParams.get("drive");
    const message = searchParams.get("message");
    if (driveResult === "connected") {
      setActionMessage("Google Drive connected successfully.");
      setSearchParams({}, { replace: true });
      void refreshStatus();
    } else if (driveResult === "error") {
      setActionError(message ? decodeURIComponent(message) : "Google Drive connection failed.");
      setSearchParams({}, { replace: true });
    }
  }, [refreshStatus, searchParams, setSearchParams]);

  async function handleConnect() {
    setActionError(null);
    try {
      const { url } = await startGoogleDriveConnect();
      window.location.href = url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to start Google Drive connection");
    }
  }

  async function handleDisconnect() {
    setActionError(null);
    setDisconnecting(true);
    try {
      await disconnectGoogleDrive();
      setActionMessage("Google Drive disconnected.");
      setDisconnectOpen(false);
      await refreshStatus();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to disconnect Google Drive");
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading && !status) {
    return (
      <section className="flex flex-1 items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-[#7c3aed] dark:text-violet-400" />
      </section>
    );
  }

  return (
    <section className="space-y-6 p-6 md:p-8">
      <div>
        <Link
          to={APPS_HOME_PATH}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--lm-muted)] transition-colors hover:text-[#0b1120] dark:text-slate-400 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to apps
        </Link>
        <p className="mt-4 text-xs uppercase tracking-[0.14em] text-[#7c3aed] dark:text-violet-300/80">
          Settings
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
          Google Drive
        </h1>
        <p className="mt-2 text-sm text-[var(--lm-muted)] dark:text-slate-400">
          Connect your personal Google account to select files from Google Drive when importing
          documents.
        </p>
      </div>

      {actionMessage ? <p className="text-sm text-emerald-600 dark:text-emerald-400">{actionMessage}</p> : null}
      {actionError ? <p className="text-sm text-red-600 dark:text-red-400">{actionError}</p> : null}

      <GlassCard className={cn("space-y-5 border p-6", visual.cardClass)}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={cn("rounded-xl p-3", visual.iconClass)}>
              <Cloud className="h-5 w-5" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                  Your connection
                </h2>
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide",
                    visual.badgeClass,
                  )}
                >
                  <span
                    className={cn(
                      "h-2 w-2 shrink-0 rounded-full",
                      visualState === "connected"
                        ? "bg-emerald-500 shadow-[0_0_8px_rgba(52,211,153,0.8)] dark:bg-emerald-400"
                        : visualState === "warning"
                          ? "bg-amber-500 shadow-[0_0_8px_rgba(251,191,36,0.8)] dark:bg-amber-400"
                          : "bg-slate-400 dark:bg-slate-500",
                    )}
                    aria-hidden
                  />
                  {statusBadgeLabel}
                </span>
              </div>
              <p className={cn("mt-1 text-sm font-medium", visual.statusTextClass)}>
                {status?.enabled
                  ? reconnectRequired
                    ? `Authorization expired for ${status.connectedAccountEmail ?? "your account"}. Reconnect to restore access.`
                    : hasActiveConnection
                      ? `Connected as ${status.connectedAccountEmail ?? "unknown account"}`
                      : "Not connected — connect your Google account to import from Drive."
                  : "Integration is disabled on this server."}
              </p>
              {reconnectRequired ? (
                <p className="mt-2 text-xs text-amber-700/90 dark:text-amber-300/90">
                  If your Google OAuth app is still in Testing mode, publish it in Google Cloud
                  Console so refresh tokens do not expire after 7 days.
                </p>
              ) : null}
              {hasActiveConnection ? (
                <p className="mt-2 text-xs text-[var(--lm-muted)] dark:text-slate-500">
                  Only files you explicitly select through Google Picker can be imported. Doculegis
                  does not have access to your full Drive.
                </p>
              ) : null}
            </div>
          </div>
          {status?.enabled ? (
            <div className="flex gap-2">
              {reconnectRequired ? (
                <Button variant="accent" onClick={() => void handleConnect()}>
                  Reconnect Google Drive
                </Button>
              ) : hasActiveConnection ? (
                <Button
                  variant="subtle"
                  size="sm"
                  className="h-8 px-4 text-[13px] font-medium"
                  onClick={() => setDisconnectOpen(true)}
                >
                  Disconnect
                </Button>
              ) : (
                <Button variant="accent" onClick={() => void handleConnect()}>
                  Connect Google Drive
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </GlassCard>

      <ConfirmDialog
        isOpen={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Google Drive"
        description="Disconnect Google Drive? You will need to reconnect to import files again."
        confirmLabel="Disconnect"
        variant="destructive"
        isLoading={disconnecting}
      />
    </section>
  );
}
